import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { Wind, Heart, Sparkles, Brain, Hand, CheckCircle2, Timer, Users } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_INFO = {
    breathing: { icon: Wind, color: 'from-sky-500 to-blue-500', label: 'Breathing' },
    grounding: { icon: Sparkles, color: 'from-emerald-500 to-teal-500', label: 'Grounding' },
    mindfulness: { icon: Brain, color: 'from-violet-500 to-purple-500', label: 'Mindfulness' },
    'self-soothing': { icon: Hand, color: 'from-rose-500 to-pink-500', label: 'Self-Soothing' },
    relaxation: { icon: Heart, color: 'from-amber-500 to-orange-500', label: 'Relaxation' },
};

export default function CopingTools() {
    const { token } = useAuth();
    const [exercises, setExercises] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeExercise, setActiveExercise] = useState(null);
    const [filter, setFilter] = useState('all');
    const [forChild, setForChild] = useState(null);

    const fetchExercises = useCallback(async () => {
        setLoading(true);
        try {
            let url = `${API_URL}/api/coping/exercises`;
            const params = [];
            if (filter !== 'all') params.push(`category=${filter}`);
            if (forChild !== null) params.push(`for_child=${forChild}`);
            if (params.length) url += '?' + params.join('&');
            
            const res = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
            if (res.ok) setExercises(await res.json());
        } catch (error) {
            console.error('Failed to fetch exercises:', error);
        } finally {
            setLoading(false);
        }
    }, [token, filter, forChild]);

    const fetchHistory = useCallback(async () => {
        try {
            const res = await fetch(`${API_URL}/api/coping/history?limit=10`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) setHistory(await res.json());
        } catch (error) {
            console.error('Failed to fetch history:', error);
        }
    }, [token]);

    useEffect(() => {
        fetchExercises();
        fetchHistory();
    }, [fetchExercises, fetchHistory]);

    const logPractice = async (exerciseId) => {
        try {
            await fetch(`${API_URL}/api/coping/log?exercise_id=${exerciseId}`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            toast.success('Practice logged!');
            fetchHistory();
        } catch (error) {
            toast.error('Failed to log');
        }
    };

    const categories = Object.keys(CATEGORY_INFO);

    return (
        <div className="space-y-6">
            <div>
                <h1 className="font-heading text-3xl font-bold">Coping Tools</h1>
                <p className="text-muted-foreground mt-1">Simple exercises for you and your child when things feel overwhelming</p>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-4">
                <Tabs value={filter} onValueChange={setFilter} className="flex-1">
                    <TabsList className="flex-wrap h-auto gap-1">
                        <TabsTrigger value="all">All</TabsTrigger>
                        {categories.map(cat => {
                            const info = CATEGORY_INFO[cat];
                            return (
                                <TabsTrigger key={cat} value={cat}>
                                    {info.label}
                                </TabsTrigger>
                            );
                        })}
                    </TabsList>
                </Tabs>
                <div className="flex gap-2">
                    <Button variant={forChild === null ? 'default' : 'outline'} size="sm" onClick={() => setForChild(null)}>All</Button>
                    <Button variant={forChild === false ? 'default' : 'outline'} size="sm" onClick={() => setForChild(false)}>For Me</Button>
                    <Button variant={forChild === true ? 'default' : 'outline'} size="sm" onClick={() => setForChild(true)}>
                        <Users className="w-4 h-4 mr-1" />
                        Kid-Friendly
                    </Button>
                </div>
            </div>

            {/* Active Exercise */}
            {activeExercise && (
                <Card className="border-2 border-primary bg-gradient-to-br from-primary/5 to-primary/10">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <CardTitle className="flex items-center gap-2">
                                {(() => {
                                    const info = CATEGORY_INFO[activeExercise.category];
                                    const Icon = info?.icon || Wind;
                                    return <Icon className="w-5 h-5" />;
                                })()}
                                {activeExercise.title}
                            </CardTitle>
                            <Button variant="ghost" onClick={() => setActiveExercise(null)}>Close</Button>
                        </div>
                        <CardDescription>{activeExercise.description}</CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        <div className="space-y-4">
                            {activeExercise.instructions.map((instruction, idx) => (
                                <div key={idx} className="flex items-start gap-4 p-4 rounded-xl bg-background/80">
                                    <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 font-bold text-primary">
                                        {idx + 1}
                                    </div>
                                    <p className="text-lg pt-1">{instruction}</p>
                                </div>
                            ))}
                        </div>
                        <div className="flex items-center justify-between pt-4 border-t">
                            <div className="flex items-center gap-2 text-muted-foreground">
                                <Timer className="w-4 h-4" />
                                <span>~{activeExercise.duration} minutes</span>
                            </div>
                            <Button onClick={() => { logPractice(activeExercise.id); setActiveExercise(null); }} className="gap-2">
                                <CheckCircle2 className="w-4 h-4" />
                                Mark as Done
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Exercise Grid */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {exercises.map((exercise) => {
                        const info = CATEGORY_INFO[exercise.category] || CATEGORY_INFO.breathing;
                        const Icon = info.icon;
                        return (
                            <Card 
                                key={exercise.id} 
                                className="card-hover cursor-pointer group"
                                onClick={() => setActiveExercise(exercise)}
                                data-testid={`exercise-${exercise.id}`}
                            >
                                <CardContent className="p-6">
                                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${info.color} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                                        <Icon className="w-6 h-6 text-white" />
                                    </div>
                                    <h3 className="font-semibold mb-1">{exercise.title}</h3>
                                    <p className="text-sm text-muted-foreground mb-3">{exercise.description}</p>
                                    <div className="flex items-center gap-2">
                                        <Badge variant="outline" className="text-xs">{info.label}</Badge>
                                        <Badge variant="outline" className="text-xs">{exercise.duration} min</Badge>
                                        {exercise.for_child && (
                                            <Badge className="bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-400 text-xs">
                                                Kid-Friendly
                                            </Badge>
                                        )}
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            )}

            {/* Recent Practice */}
            {history.length > 0 && (
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">Recent Practice</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-wrap gap-2">
                            {history.map((log) => (
                                <Badge key={log.id} variant="secondary" className="py-2 px-3">
                                    <CheckCircle2 className="w-3 h-3 mr-1 text-emerald-500" />
                                    {log.exercise_title}
                                </Badge>
                            ))}
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
}
